import React, {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { useHistory, useParams } from "react-router-dom";
import "emoji-mart/css/emoji-mart.css";
import {
  Avatar,
  Box,
  Button,
  Chip,
  ClickAwayListener,
  CircularProgress,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Typography,
  useMediaQuery,
  makeStyles
} from "@material-ui/core";
import Tooltip from "@material-ui/core/Tooltip";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import { Picker } from "emoji-mart";
import SearchIcon from "@material-ui/icons/Search";
import ForumOutlinedIcon from "@material-ui/icons/ForumOutlined";
import GroupOutlinedIcon from "@material-ui/icons/GroupOutlined";
import SendOutlinedIcon from "@material-ui/icons/SendOutlined";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import AttachFileOutlinedIcon from "@material-ui/icons/AttachFileOutlined";
import MoodOutlinedIcon from "@material-ui/icons/MoodOutlined";
import MicOutlinedIcon from "@material-ui/icons/MicOutlined";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import HighlightOffIcon from "@material-ui/icons/HighlightOff";
import InsertDriveFileOutlinedIcon from "@material-ui/icons/InsertDriveFileOutlined";
import ImageOutlinedIcon from "@material-ui/icons/ImageOutlined";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import Audio from "../../components/Audio";
import RecordingTimer from "../../components/MessageInput/RecordingTimer";
import InternalChatConversationDialog from "../../components/InternalChatConversationDialog";
import ConfirmationModal from "../../components/ConfirmationModal";
import { toast } from "react-toastify";
import { AuthContext } from "../../context/Auth/AuthContext";
import { InternalChatNotificationsContext } from "../../context/InternalChatNotifications/InternalChatNotificationsContext";
import api from "../../services/api";
import openSocket from "../../services/socket-io";
import toastError from "../../errors/toastError";
import { useTheme } from "@material-ui/core/styles";

const useStyles = makeStyles(theme => ({
  shell: {
    flex: 1,
    minHeight: 0,
    display: "grid",
    gridTemplateColumns: "340px minmax(0, 1fr)",
    gap: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "minmax(0, 1fr)",
      gap: theme.spacing(1.25)
    }
  },
  sidebar: {
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    borderRadius: 24,
    background:
      theme.palette.type === "dark"
        ? "linear-gradient(180deg, rgba(15, 23, 42, 0.96) 0%, rgba(17, 24, 39, 0.92) 100%)"
        : "linear-gradient(180deg, rgba(255, 255, 255, 0.98) 0%, rgba(247, 250, 252, 0.98) 100%)",
    boxShadow:
      theme.palette.type === "dark"
        ? "0 18px 40px rgba(2, 6, 23, 0.35)"
        : "0 18px 40px rgba(15, 23, 42, 0.08)",
    [theme.breakpoints.down("sm")]: {
      borderRadius: 22
    }
  },
  sidebarHeader: {
    padding: theme.spacing(2, 2, 1.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
    background:
      theme.palette.type === "dark"
        ? "linear-gradient(135deg, rgba(15, 23, 42, 0.92) 0%, rgba(17, 94, 89, 0.16) 100%)"
        : "linear-gradient(135deg, rgba(255, 255, 255, 0.96) 0%, rgba(240, 253, 250, 0.96) 100%)"
  },
  sidebarBody: {
    overflowY: "auto",
    ...theme.scrollbarStyles
  },
  conversationItem: {
    width: "100%",
    border: 0,
    background: "transparent",
    color: theme.palette.text.primary,
    textAlign: "left",
    padding: theme.spacing(1.5, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    cursor: "pointer",
    transition: "background-color 0.18s ease, border-left-color 0.18s ease, box-shadow 0.18s ease",
    borderLeft: "4px solid transparent",
    "&:hover": {
      backgroundColor:
        theme.palette.type === "dark"
          ? "rgba(45, 212, 191, 0.08)"
          : "rgba(15, 118, 110, 0.06)"
    },
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(1.25, 1.25)
    }
  },
  conversationItemActive: {
    background:
      theme.palette.type === "dark"
        ? "linear-gradient(90deg, rgba(20, 184, 166, 0.18) 0%, rgba(15, 23, 42, 0.18) 100%)"
        : "linear-gradient(90deg, rgba(204, 251, 241, 0.95) 0%, rgba(240, 249, 255, 0.85) 100%)",
    borderLeftColor: theme.palette.primary.main,
    boxShadow:
      theme.palette.type === "dark"
        ? "inset 0 1px 0 rgba(255, 255, 255, 0.04)"
        : "inset 0 1px 0 rgba(255, 255, 255, 0.75)"
  },
  conversationTop: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.25),
    minWidth: 0
  },
  avatar: {
    width: 42,
    height: 42,
    fontWeight: 700,
    color: theme.palette.type === "dark" ? theme.palette.common.white : theme.palette.primary.dark,
    background:
      theme.palette.type === "dark"
        ? "linear-gradient(135deg, rgba(71, 85, 105, 0.92) 0%, rgba(30, 41, 59, 0.96) 100%)"
        : "linear-gradient(135deg, rgba(226, 232, 240, 0.95) 0%, rgba(203, 213, 225, 0.92) 100%)",
    [theme.breakpoints.down("sm")]: {
      width: 38,
      height: 38
    }
  },
  conversationMeta: {
    minWidth: 0,
    flex: 1
  },
  conversationTitleRow: {
    display: "flex",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    alignItems: "center"
  },
  conversationTitleGroup: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.75),
    minWidth: 0
  },
  conversationTitle: {
    fontWeight: 700,
    color: theme.palette.text.primary,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis"
  },
  conversationTime: {
    color: theme.palette.text.secondary,
    fontSize: 12,
    whiteSpace: "nowrap"
  },
  conversationSubtitle: {
    color: theme.palette.text.secondary,
    fontSize: 13,
    marginTop: theme.spacing(0.25),
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis"
  },
  conversationSubtitleUnread: {
    color: theme.palette.text.primary,
    fontWeight: 700
  },
  conversationChips: {
    display: "flex",
    gap: theme.spacing(0.75),
    flexWrap: "wrap",
    marginTop: theme.spacing(1)
  },
  chipBase: {
    height: 24,
    borderRadius: 999,
    fontWeight: 700,
    border: 0,
    "& .MuiChip-label": {
      paddingLeft: 10,
      paddingRight: 10
    }
  },
  chipGroup: {
    color: "#0f766e",
    backgroundColor: "rgba(204, 251, 241, 0.95)"
  },
  chipDirect: {
    color: "#1d4ed8",
    backgroundColor: "rgba(219, 234, 254, 0.95)"
  },
  chipMeta: {
    color: theme.palette.text.primary,
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(71, 85, 105, 0.28)"
        : "rgba(226, 232, 240, 0.95)"
  },
  chipUnread: {
    color: "#7c2d12",
    backgroundColor: "rgba(254, 215, 170, 0.98)"
  },
  chatPanel: {
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    borderRadius: 28,
    background:
      theme.palette.type === "dark"
        ? "linear-gradient(180deg, rgba(15, 23, 42, 0.95) 0%, rgba(15, 23, 42, 0.9) 100%)"
        : "linear-gradient(180deg, rgba(255, 255, 255, 0.99) 0%, rgba(248, 250, 252, 0.99) 100%)",
    boxShadow:
      theme.palette.type === "dark"
        ? "0 24px 50px rgba(2, 6, 23, 0.38)"
        : "0 24px 50px rgba(15, 23, 42, 0.08)",
    [theme.breakpoints.down("sm")]: {
      borderRadius: 22
    }
  },
  chatHeader: {
    padding: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    background:
      theme.palette.type === "dark"
        ? "linear-gradient(135deg, rgba(15, 23, 42, 0.96) 0%, rgba(20, 184, 166, 0.12) 100%)"
        : "linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(240, 253, 250, 0.98) 100%)",
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(1.25),
      alignItems: "flex-start",
      flexDirection: "column"
    }
  },
  chatHeaderInfo: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    minWidth: 0,
    width: "100%"
  },
  chatHeaderText: {
    minWidth: 0
  },
  chatHeaderTitleRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.75),
    minWidth: 0
  },
  chatTitle: {
    fontWeight: 800
  },
  chatSubtitle: {
    color: theme.palette.text.secondary,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    [theme.breakpoints.down("sm")]: {
      whiteSpace: "normal"
    }
  },
  messagesArea: {
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
    padding: theme.spacing(2),
    position: "relative",
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.25),
    background:
      theme.palette.type === "dark"
        ? "linear-gradient(180deg, rgba(2, 44, 34, 0.22) 0%, rgba(15, 23, 42, 0.18) 100%)"
        : "linear-gradient(180deg, rgba(240, 253, 250, 0.92) 0%, rgba(248, 250, 252, 0.95) 100%)",
    "&::before": {
      content: '""',
      position: "absolute",
      inset: 0,
      pointerEvents: "none",
      background:
        theme.palette.type === "dark"
          ? "radial-gradient(circle at top left, rgba(45, 212, 191, 0.06), transparent 34%), radial-gradient(circle at bottom right, rgba(59, 130, 246, 0.06), transparent 32%)"
          : "radial-gradient(circle at top left, rgba(20, 184, 166, 0.08), transparent 34%), radial-gradient(circle at bottom right, rgba(59, 130, 246, 0.06), transparent 30%)"
    },
    ...theme.scrollbarStyles
  },
  messageRowMine: {
    display: "flex",
    justifyContent: "flex-end",
    position: "relative",
    zIndex: 1
  },
  messageRowOther: {
    display: "flex",
    justifyContent: "flex-start",
    position: "relative",
    zIndex: 1
  },
  messageBubble: {
    maxWidth: "72%",
    padding: theme.spacing(1.25, 1.5),
    borderRadius: 18,
    boxShadow:
      theme.palette.type === "dark"
        ? "0 10px 24px rgba(2, 6, 23, 0.28)"
        : "0 10px 24px rgba(15, 23, 42, 0.08)",
    border: `1px solid ${theme.palette.divider}`,
    [theme.breakpoints.down("sm")]: {
      maxWidth: "88%"
    }
  },
  messageBubbleMine: {
    background:
      theme.palette.type === "dark"
        ? "linear-gradient(180deg, rgba(20, 184, 166, 0.24) 0%, rgba(15, 118, 110, 0.18) 100%)"
        : "linear-gradient(180deg, rgba(220, 252, 231, 0.98) 0%, rgba(209, 250, 229, 0.96) 100%)"
  },
  messageBubbleOther: {
    background:
      theme.palette.type === "dark"
        ? "linear-gradient(180deg, rgba(30, 41, 59, 0.94) 0%, rgba(15, 23, 42, 0.92) 100%)"
        : "linear-gradient(180deg, rgba(255, 255, 255, 0.99) 0%, rgba(248, 250, 252, 0.98) 100%)"
  },
  messageAuthor: {
    fontWeight: 700,
    marginBottom: theme.spacing(0.5),
    color: theme.palette.primary.main
  },
  messageBody: {
    whiteSpace: "pre-wrap",
    wordBreak: "break-word"
  },
  messageTime: {
    display: "block",
    marginTop: theme.spacing(0.75),
    color: theme.palette.text.secondary,
    fontSize: 12,
    textAlign: "right"
  },
  composer: {
    padding: theme.spacing(1.5, 2),
    borderTop: `1px solid ${theme.palette.divider}`,
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.25),
    background:
      theme.palette.type === "dark"
        ? "linear-gradient(180deg, rgba(15, 23, 42, 0.98) 0%, rgba(17, 24, 39, 0.98) 100%)"
        : "linear-gradient(180deg, rgba(255, 255, 255, 0.98) 0%, rgba(248, 250, 252, 0.98) 100%)"
  },
  composerRow: {
    width: "100%",
    display: "flex",
    gap: theme.spacing(1.25),
    alignItems: "flex-end",
    [theme.breakpoints.down("sm")]: {
      flexWrap: "wrap",
      gap: theme.spacing(1)
    }
  },
  composerActions: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      justifyContent: "space-between"
    }
  },
  composerInput: {
    flex: 1,
    [theme.breakpoints.down("sm")]: {
      width: "100%"
    }
  },
  composerInputWrap: {
    position: "relative",
    flex: 1
  },
  iconActionButton: {
    color: theme.palette.text.secondary
  },
  sendButton: {
    width: 42,
    height: 42,
    color: theme.palette.common.white,
    background:
      theme.palette.type === "dark"
        ? "linear-gradient(135deg, #14b8a6 0%, #0f766e 100%)"
        : "linear-gradient(135deg, #0f766e 0%, #115e59 100%)",
    boxShadow:
      theme.palette.type === "dark"
        ? "0 12px 24px rgba(20, 184, 166, 0.22)"
        : "0 12px 24px rgba(15, 118, 110, 0.22)",
    "&:hover": {
      background:
        theme.palette.type === "dark"
          ? "linear-gradient(135deg, #0f766e 0%, #134e4a 100%)"
          : "linear-gradient(135deg, #115e59 0%, #134e4a 100%)"
    },
    "&.Mui-disabled": {
      color: theme.palette.action.disabled,
      background: theme.palette.action.disabledBackground,
      boxShadow: "none"
    }
  },
  attachmentInput: {
    display: "none"
  },
  attachmentPreview: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    padding: theme.spacing(1, 1.25),
    borderRadius: 16,
    border: `1px solid ${theme.palette.divider}`,
    background:
      theme.palette.type === "dark"
        ? "rgba(30, 41, 59, 0.88)"
        : "rgba(248, 250, 252, 0.98)"
  },
  attachmentPreviewInfo: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    minWidth: 0
  },
  attachmentPreviewText: {
    minWidth: 0
  },
  attachmentName: {
    fontWeight: 700,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis"
  },
  attachmentMeta: {
    color: theme.palette.text.secondary,
    fontSize: 12
  },
  emojiBox: {
    position: "absolute",
    bottom: "100%",
    left: 0,
    zIndex: 20,
    marginBottom: theme.spacing(1)
  },
  recorderWrapper: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    padding: theme.spacing(0.5, 0),
    [theme.breakpoints.down("sm")]: {
      flexWrap: "wrap"
    }
  },
  audioLoading: {
    color: theme.palette.primary.main
  },
  audioCard: {
    minWidth: 280,
    maxWidth: "100%",
    [theme.breakpoints.down("sm")]: {
      minWidth: 0
    }
  },
  imagePreview: {
    display: "block",
    maxWidth: "100%",
    maxHeight: 320,
    borderRadius: 14
  },
  documentCard: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    textDecoration: "none",
    color: "inherit",
    padding: theme.spacing(1),
    borderRadius: 14,
    border: `1px solid ${theme.palette.divider}`,
    background:
      theme.palette.type === "dark"
        ? "rgba(15, 23, 42, 0.4)"
        : "rgba(255, 255, 255, 0.7)"
  },
  documentName: {
    fontWeight: 700,
    wordBreak: "break-word"
  },
  emptyState: {
    flex: 1,
    minHeight: 240,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    padding: theme.spacing(4),
    position: "relative",
    zIndex: 1
  },
  presenceDot: {
    width: 10,
    height: 10,
    borderRadius: "50%",
    flexShrink: 0,
    boxShadow:
      theme.palette.type === "dark"
        ? "0 0 0 3px rgba(15, 23, 42, 0.88)"
        : "0 0 0 3px rgba(255, 255, 255, 0.95)"
  },
  headerActions: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      justifyContent: "space-between",
      flexWrap: "wrap"
    }
  },
  subtleActionButton: {
    color: theme.palette.text.secondary
  },
  mobileBackButton: {
    color: theme.palette.text.secondary
  }
}));

const getSupportedAudioMimeType = () => {
  if (typeof window === "undefined" || typeof window.MediaRecorder === "undefined") {
    return "";
  }

  const candidates = [
    "audio/ogg;codecs=opus",
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/mp4"
  ];

  return candidates.find(type => window.MediaRecorder.isTypeSupported(type)) || "";
};

const getAudioFileExtension = mimeType => {
  if (!mimeType) {
    return "webm";
  }

  if (mimeType.includes("ogg")) {
    return "ogg";
  }

  if (mimeType.includes("mp4")) {
    return "mp4";
  }

  return "webm";
};

const formatBytes = size => {
  if (!size && size !== 0) {
    return "";
  }

  if (size < 1024) {
    return `${size} B`;
  }

  if (size < 1024 * 1024) {
    return `${(size / 1024).toFixed(1)} KB`;
  }

  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

const getPendingMediaLabel = file => {
  if (!file) {
    return "";
  }

  const mimeType = String(file.type || "").toLowerCase();

  if (mimeType.startsWith("image/")) {
    return "Imagem pronta para envio";
  }

  if (mimeType.startsWith("audio/")) {
    return "Áudio pronto para envio";
  }

  if (mimeType === "application/pdf") {
    return "PDF pronto para envio";
  }

  return "Arquivo pronto para envio";
};

const getPresenceColor = status => {
  switch (status) {
    case "online":
      return "#22c55e";
    case "away":
      return "#f59e0b";
    default:
      return "#ef4444";
  }
};

const formatConversationTime = value => {
  if (!value) {
    return "";
  }

  return new Date(value).toLocaleTimeString("pt-BR", {
    hour: "2-digit",
    minute: "2-digit"
  });
};

const formatConversationSubtitle = conversation => {
  if (conversation.lastMessage) {
    return conversation.lastMessage;
  }

  return conversation.type === "group"
    ? "Grupo interno sem mensagens"
    : "Conversa interna sem mensagens";
};

const getCounterpart = (conversation, currentUserId) => {
  const participants = Array.isArray(conversation?.participants)
    ? conversation.participants
    : [];

  return (
    participants.find(
      participant => Number(participant.userId) !== Number(currentUserId)
    )?.user || null
  );
};

const getConversationTitle = (conversation, currentUserId) => {
  if (conversation.type === "group") {
    return conversation.name || "Grupo interno";
  }

  const counterpart = getCounterpart(conversation, currentUserId);
  return counterpart?.name || "Conversa interna";
};

const getConversationSubtitleMeta = (conversation, currentUserId) => {
  if (conversation.type === "group") {
    const participantNames = (conversation.participants || [])
      .map(participant => participant.user?.name)
      .filter(Boolean);
    return participantNames.join(", ");
  }

  const counterpart = getCounterpart(conversation, currentUserId);
  return counterpart?.email || "Sem participante definido";
};

const isConversationVisibleForUser = (conversation, currentUserId) => {
  if (!conversation || !currentUserId) {
    return false;
  }

  const participantIds = Array.isArray(conversation.participantUserIds)
    ? conversation.participantUserIds.map(Number)
    : [];

  if (participantIds.length > 0) {
    return participantIds.includes(Number(currentUserId));
  }

  const participants = Array.isArray(conversation.participants)
    ? conversation.participants
    : [];

  return participants.some(
    participant => Number(participant?.userId) === Number(currentUserId)
  );
};

const getConversationPresenceStatus = (conversation, currentUserId) => {
  if (!conversation) {
    return "offline";
  }

  if (conversation.type !== "group") {
    return getCounterpart(conversation, currentUserId)?.presenceStatus || "offline";
  }

  const participantStatuses = (conversation.participants || [])
    .filter(participant => Number(participant.userId) !== Number(currentUserId))
    .map(participant => participant?.user?.presenceStatus)
    .filter(Boolean);

  if (participantStatuses.includes("online")) {
    return "online";
  }

  if (participantStatuses.includes("away")) {
    return "away";
  }

  return "offline";
};

const applyPresenceToConversation = (conversation, presence) => {
  if (!conversation || !presence) {
    return conversation;
  }

  const targetUserId = Number(presence.userId);
  let hasChanges = false;

  const participants = (conversation.participants || []).map(participant => {
    if (Number(participant.userId) !== targetUserId || !participant.user) {
      return participant;
    }

    hasChanges = true;

    return {
      ...participant,
      user: {
        ...participant.user,
        presenceStatus: presence.status,
        lastActivityAt: presence.lastActivityAt,
        lastHeartbeatAt: presence.lastHeartbeatAt,
        lastOfflineAt: presence.lastOfflineAt
      }
    };
  });

  return hasChanges ? { ...conversation, participants } : conversation;
};

const mergeConversation = (items, incomingConversation) => {
  const normalizedItems = Array.isArray(items) ? items : [];
  const incomingId = Number(incomingConversation?.id);
  const nextItems = normalizedItems.filter(
    item => Number(item.id) !== incomingId
  );

  nextItems.unshift(incomingConversation);

  return nextItems.sort((left, right) => {
    const leftTime = new Date(left.lastMessageAt || left.updatedAt || 0).getTime();
    const rightTime = new Date(right.lastMessageAt || right.updatedAt || 0).getTime();
    return rightTime - leftTime;
  });
};

const upsertMessage = (items, incomingMessage) => {
  const normalizedItems = Array.isArray(items) ? items : [];
  if (normalizedItems.some(item => Number(item.id) === Number(incomingMessage.id))) {
    return normalizedItems;
  }

  return [...normalizedItems, incomingMessage];
};

const removeConversation = (items, conversationId) =>
  (Array.isArray(items) ? items : []).filter(
    item => Number(item.id) !== Number(conversationId)
  );

const InternalChat = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const history = useHistory();
  const { conversationId } = useParams();
  const { user } = useContext(AuthContext);
  const { unreadByConversation, markConversationRead } = useContext(
    InternalChatNotificationsContext
  );
  const socketRef = useRef(null);
  const messagesAreaRef = useRef(null);
  const activeConversationIdRef = useRef(null);
  const fileInputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioMimeTypeRef = useRef("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loadingConversations, setLoadingConversations] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sending, setSending] = useState(false);
  const [users, setUsers] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [searchParam, setSearchParam] = useState("");
  const [composeBody, setComposeBody] = useState("");
  const [showEmoji, setShowEmoji] = useState(false);
  const [pendingMedia, setPendingMedia] = useState(null);
  const [recording, setRecording] = useState(false);
  const [startingRecording, setStartingRecording] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const canManageGroups = user?.profile === "admin";

  const activeConversationId = conversationId ? Number(conversationId) : null;

  useEffect(() => {
    activeConversationIdRef.current = activeConversationId;
  }, [activeConversationId]);

  const activeConversation = useMemo(
    () =>
      conversations.find(
        conversation => Number(conversation.id) === Number(activeConversationId)
      ) || null,
    [conversations, activeConversationId]
  );

  const shouldShowSidebar = !isMobile || !activeConversation;
  const shouldShowChatPanel = !isMobile || Boolean(activeConversation);

  const filteredConversations = useMemo(() => {
    const normalizedSearch = String(searchParam || "").trim().toLowerCase();
    if (!normalizedSearch) {
      return conversations;
    }

    return conversations.filter(conversation => {
      const haystack = [
        getConversationTitle(conversation, user?.id),
        formatConversationSubtitle(conversation),
        getConversationSubtitleMeta(conversation, user?.id)
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return haystack.includes(normalizedSearch);
    });
  }, [conversations, searchParam, user?.id]);

  const fetchBaseData = useCallback(async () => {
    setLoadingConversations(true);
    try {
      const [conversationsResponse, usersResponse] = await Promise.all([
        api.get("/internal-chats"),
        api.get("/internal-chats/users")
      ]);

      setConversations(
        (Array.isArray(conversationsResponse.data)
          ? conversationsResponse.data
          : []
        ).filter(conversation =>
          isConversationVisibleForUser(conversation, user?.id)
        )
      );
      setUsers(Array.isArray(usersResponse.data) ? usersResponse.data : []);
    } catch (error) {
      toastError(error);
    } finally {
      setLoadingConversations(false);
    }
  }, [user?.id]);

  const fetchMessages = useCallback(async currentConversationId => {
    if (!currentConversationId) {
      setMessages([]);
      return;
    }

    setLoadingMessages(true);
    try {
      const { data } = await api.get(
        `/internal-chats/${currentConversationId}/messages`
      );
      setMessages(Array.isArray(data) ? data : []);
    } catch (error) {
      toastError(error);
      history.replace("/internal-chat");
    } finally {
      setLoadingMessages(false);
    }
  }, [history]);

  useEffect(() => {
    fetchBaseData();
  }, [fetchBaseData]);

  useEffect(() => {
    fetchMessages(activeConversationId);
  }, [activeConversationId, fetchMessages]);

  useEffect(() => {
    if (activeConversationId) {
      markConversationRead(activeConversationId);
    }
  }, [activeConversationId, markConversationRead]);

  const scrollMessagesToBottom = useCallback((behavior = "auto") => {
    const container = messagesAreaRef.current;
    if (!container) {
      return;
    }

    if (behavior === "smooth" && "scrollTo" in container) {
      container.scrollTo({
        top: container.scrollHeight,
        behavior
      });
      return;
    }

    container.scrollTop = container.scrollHeight;
  }, []);

  useLayoutEffect(() => {
    if (loadingMessages) {
      return;
    }

    const animationFrame = window.requestAnimationFrame(() => {
      scrollMessagesToBottom("auto");
    });

    return () => {
      window.cancelAnimationFrame(animationFrame);
    };
  }, [activeConversationId, loadingMessages, messages.length, scrollMessagesToBottom]);

  useEffect(() => {
    if (!user?.id) {
      return undefined;
    }

    const socket = openSocket();
    socketRef.current = socket;

    socket.on("internalChat:conversation", data => {
      if (data?.action === "update" && data?.conversation) {
        if (!isConversationVisibleForUser(data.conversation, user?.id)) {
          return;
        }
        setConversations(prev => mergeConversation(prev, data.conversation));
        return;
      }

      if (data?.action === "remove" && data?.conversationId) {
        setConversations(prev => removeConversation(prev, data.conversationId));

        if (Number(activeConversationIdRef.current) === Number(data.conversationId)) {
          setMessages([]);
          history.replace("/internal-chat");
        }
      }
    });

    socket.on("internalChat:message", payload => {
      if (
        payload?.conversation &&
        !isConversationVisibleForUser(payload.conversation, user?.id)
      ) {
        return;
      }

      if (payload?.conversation) {
        setConversations(prev => mergeConversation(prev, payload.conversation));
      }

      if (payload?.message) {
        setMessages(prev => {
          if (
            Number(payload.message.conversationId) !==
            Number(activeConversationIdRef.current)
          ) {
            return prev;
          }

          return upsertMessage(prev, payload.message);
        });
      }
    });

    socket.on("userPresence", data => {
      if (data?.action !== "update" || !data?.presence) {
        return;
      }

      setUsers(prev =>
        (Array.isArray(prev) ? prev : []).map(currentUser =>
          Number(currentUser.id) !== Number(data.presence.userId)
            ? currentUser
            : {
                ...currentUser,
                presenceStatus: data.presence.status,
                lastActivityAt: data.presence.lastActivityAt,
                lastHeartbeatAt: data.presence.lastHeartbeatAt,
                lastOfflineAt: data.presence.lastOfflineAt
              }
        )
      );

      setConversations(prev =>
        (Array.isArray(prev) ? prev : []).map(conversation =>
          applyPresenceToConversation(conversation, data.presence)
        )
      );
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, [history, user?.id]);

  useEffect(() => {
    if (socketRef.current && activeConversationId) {
      socketRef.current.emit("joinInternalChat", activeConversationId);
    }
  }, [activeConversationId]);

  const handleOpenConversation = conversation => {
    history.push(`/internal-chat/${conversation.id}`);
  };

  const handleCreatedConversation = conversation => {
    setConversations(prev => mergeConversation(prev, conversation));
    history.push(`/internal-chat/${conversation.id}`);
  };

  const handleDeleteConversation = async () => {
    if (!activeConversation) {
      return;
    }

    try {
      await api.delete(`/internal-chats/${activeConversation.id}`);
      setConversations(prev => removeConversation(prev, activeConversation.id));
      setMessages([]);
      setDeleteConfirmOpen(false);
      history.replace("/internal-chat");
    } catch (error) {
      toastError(error);
    }
  };

  const resetRecorderResources = useCallback(() => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.ondataavailable = null;
      mediaRecorderRef.current.onstop = null;
      mediaRecorderRef.current.onerror = null;
    }

    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach(track => track.stop());
    }

    mediaRecorderRef.current = null;
    mediaStreamRef.current = null;
    audioChunksRef.current = [];
    audioMimeTypeRef.current = "";
  }, []);

  useEffect(() => {
    setPendingMedia(null);
    setShowEmoji(false);
    setComposeBody("");
    setRecording(false);
    setStartingRecording(false);
    resetRecorderResources();
  }, [activeConversationId, resetRecorderResources]);

  const stopRecordingAndGetBlob = useCallback(() => {
    return new Promise((resolve, reject) => {
      const recorder = mediaRecorderRef.current;
      const mimeType =
        audioMimeTypeRef.current || recorder?.mimeType || "audio/webm";

      if (!recorder) {
        reject(new Error("ERR_INTERNAL_CHAT_RECORDER_NOT_READY"));
        return;
      }

      if (recorder.state === "inactive") {
        const blob = new Blob(audioChunksRef.current, { type: mimeType });
        resetRecorderResources();
        resolve(blob);
        return;
      }

      recorder.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: mimeType });
        resetRecorderResources();
        resolve(blob);
      };

      recorder.onerror = event => {
        resetRecorderResources();
        reject(event?.error || new Error("ERR_INTERNAL_CHAT_RECORDING_STOP"));
      };

      recorder.stop();
    });
  }, [resetRecorderResources]);

  useEffect(() => () => {
    resetRecorderResources();
  }, [resetRecorderResources]);

  const sendMediaFile = useCallback(
    async file => {
      const formData = new FormData();
      formData.append("medias", file);
      formData.append("body", "");

      const { data } = await api.post(
        `/internal-chats/${activeConversationId}/messages`,
        formData
      );

      if (data?.message) {
        setMessages(prev => upsertMessage(prev, data.message));
      }
      if (data?.conversation) {
        setConversations(prev => mergeConversation(prev, data.conversation));
      }
    },
    [activeConversationId]
  );

  const handleSendMessage = async () => {
    if (!activeConversationId || !String(composeBody || "").trim()) {
      return;
    }

    setSending(true);
    try {
      const { data } = await api.post(
        `/internal-chats/${activeConversationId}/messages`,
        {
          body: composeBody
        }
      );

      setComposeBody("");
      setShowEmoji(false);
      if (data?.message) {
        setMessages(prev => upsertMessage(prev, data.message));
      }
      if (data?.conversation) {
        setConversations(prev => mergeConversation(prev, data.conversation));
      }
    } catch (error) {
      toastError(error);
    } finally {
      setSending(false);
    }
  };

  const handleSelectMedia = event => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) {
      return;
    }

    const allowedMimeTypes = [
      "image/jpeg",
      "image/png",
      "application/pdf"
    ];

    if (!allowedMimeTypes.includes(selectedFile.type)) {
      toast.error("Apenas JPEG, PNG e PDF são permitidos no chat interno.");
      event.target.value = "";
      return;
    }

    setPendingMedia(selectedFile);
    event.target.value = "";
  };

  const handleSendPendingMedia = async () => {
    if (!pendingMedia || !activeConversationId) {
      return;
    }

    setSending(true);
    try {
      await sendMediaFile(pendingMedia);
      setPendingMedia(null);
      setShowEmoji(false);
    } catch (error) {
      toastError(error);
    } finally {
      setSending(false);
    }
  };

  const handleAddEmoji = emoji => {
    setComposeBody(prev => `${prev}${emoji.native}`);
  };

  const handleStartRecording = async () => {
    setStartingRecording(true);
    try {
      if (
        typeof navigator === "undefined" ||
        !navigator.mediaDevices?.getUserMedia ||
        typeof window === "undefined" ||
        typeof window.MediaRecorder === "undefined"
      ) {
        throw new Error("Gravação de áudio não suportada neste navegador.");
      }

      resetRecorderResources();

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = getSupportedAudioMimeType();
      const recorder = mimeType
        ? new window.MediaRecorder(stream, { mimeType })
        : new window.MediaRecorder(stream);

      mediaStreamRef.current = stream;
      mediaRecorderRef.current = recorder;
      audioChunksRef.current = [];
      audioMimeTypeRef.current = recorder.mimeType || mimeType || "audio/webm";

      recorder.onstart = () => {
        setRecording(true);
        setStartingRecording(false);
      };

      recorder.ondataavailable = mediaEvent => {
        if (mediaEvent.data && mediaEvent.data.size > 0) {
          audioChunksRef.current.push(mediaEvent.data);
        }
      };

      recorder.onerror = () => {
        resetRecorderResources();
        setRecording(false);
        setStartingRecording(false);
      };

      recorder.start();
    } catch (error) {
      toastError(error);
      setStartingRecording(false);
    }
  };

  const handleSendRecordedAudio = async () => {
    setSending(true);
    try {
      const blob = await stopRecordingAndGetBlob();
      if (blob.size < 1000) {
        setRecording(false);
        setSending(false);
        return;
      }

      const extension = getAudioFileExtension(blob.type);
      const filename = `${new Date().getTime()}.${extension}`;
      const file = new File([blob], filename, {
        type: blob.type || "audio/webm"
      });

      await sendMediaFile(file);
      setRecording(false);
      setShowEmoji(false);
    } catch (error) {
      toastError(error);
    } finally {
      setSending(false);
      setStartingRecording(false);
    }
  };

  const handleCancelAudio = async () => {
    try {
      if (mediaRecorderRef.current?.state === "recording") {
        await stopRecordingAndGetBlob();
      } else {
        resetRecorderResources();
      }
    } catch (error) {
      toastError(error);
    } finally {
      setRecording(false);
      setStartingRecording(false);
    }
  };

  const handleComposerKeyDown = event => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <MainContainer>
      <InternalChatConversationDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        users={users}
        onCreated={handleCreatedConversation}
        canManageGroups={canManageGroups}
      />
      <ConfirmationModal
        title="Apagar conversa"
        open={deleteConfirmOpen}
        onClose={setDeleteConfirmOpen}
        onConfirm={handleDeleteConversation}
      >
        {activeConversation?.type === "group"
          ? "Deseja apagar este grupo interno para todos os participantes?"
          : "Deseja apagar esta conversa individual?"}
      </ConfirmationModal>

      <MainHeader>
        <Title>Chat interno</Title>
        <MainHeaderButtonsWrapper>
          <Button variant="outlined" color="primary" onClick={fetchBaseData}>
            Atualizar
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setDialogOpen(true)}
          >
            {canManageGroups ? "Nova conversa" : "Nova conversa individual"}
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <div className={classes.shell}>
        {shouldShowSidebar ? (
        <Paper variant="outlined" className={classes.sidebar}>
          <div className={classes.sidebarHeader}>
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              placeholder="Buscar conversa"
              value={searchParam}
              onChange={event => setSearchParam(event.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                )
              }}
            />
          </div>
          <div className={classes.sidebarBody}>
            {loadingConversations ? (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight={220}
              >
                <CircularProgress size={28} />
              </Box>
            ) : filteredConversations.length ? (
              filteredConversations.map(conversation => {
                const title = getConversationTitle(conversation, user?.id);
                const subtitleMeta = getConversationSubtitleMeta(
                  conversation,
                  user?.id
                );
                const unreadCount = unreadByConversation[conversation.id] || 0;
                const isActive =
                  Number(conversation.id) === Number(activeConversationId);

                return (
                  <button
                    type="button"
                    key={conversation.id}
                    className={`${classes.conversationItem} ${
                      isActive ? classes.conversationItemActive : ""
                    }`}
                    onClick={() => handleOpenConversation(conversation)}
                  >
                    <div className={classes.conversationTop}>
                      <Avatar className={classes.avatar}>
                        {conversation.type === "group"
                          ? "#"
                          : String(title || "?").charAt(0).toUpperCase()}
                      </Avatar>

                      <div className={classes.conversationMeta}>
                        <div className={classes.conversationTitleRow}>
                          <div className={classes.conversationTitleGroup}>
                            <span
                              className={classes.presenceDot}
                              style={{
                                backgroundColor: getPresenceColor(
                                  getConversationPresenceStatus(
                                    conversation,
                                    user?.id
                                  )
                                )
                              }}
                            />
                            <Typography className={classes.conversationTitle}>
                              {title}
                            </Typography>
                          </div>
                          <Typography className={classes.conversationTime}>
                            {formatConversationTime(
                              conversation.lastMessageAt || conversation.updatedAt
                            )}
                          </Typography>
                        </div>
                        <Typography
                          className={`${classes.conversationSubtitle} ${
                            unreadCount ? classes.conversationSubtitleUnread : ""
                          }`}
                        >
                          {formatConversationSubtitle(conversation)}
                        </Typography>
                        <div className={classes.conversationChips}>
                          <Chip
                            size="small"
                            className={`${classes.chipBase} ${
                              conversation.type === "group"
                                ? classes.chipGroup
                                : classes.chipDirect
                            }`}
                            label={
                              conversation.type === "group"
                                ? "Grupo"
                                : "Individual"
                            }
                            icon={
                              conversation.type === "group" ? (
                                <GroupOutlinedIcon fontSize="small" />
                              ) : (
                                <ForumOutlinedIcon fontSize="small" />
                              )
                            }
                          />
                          <Chip
                            size="small"
                            className={`${classes.chipBase} ${classes.chipMeta}`}
                            label={subtitleMeta}
                          />
                          {unreadCount ? (
                            <Chip
                              size="small"
                              className={`${classes.chipBase} ${classes.chipUnread}`}
                              label={`${unreadCount} nova${
                                unreadCount > 1 ? "s" : ""
                              }`}
                            />
                          ) : null}
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })
            ) : (
              <div className={classes.emptyState}>
                <div>
                  <Typography variant="h6">Nenhuma conversa interna</Typography>
                  <Typography variant="body2" color="textSecondary">
                    {canManageGroups
                      ? "Crie uma conversa individual ou um grupo para sua equipe."
                      : "Crie uma conversa individual para falar com sua equipe."}
                  </Typography>
                </div>
              </div>
            )}
          </div>
        </Paper>
        ) : null}

        {shouldShowChatPanel ? (
        <Paper variant="outlined" className={classes.chatPanel}>
          {activeConversation ? (
            <>
              <div className={classes.chatHeader}>
                <div className={classes.chatHeaderInfo}>
                  {isMobile ? (
                    <IconButton
                      className={classes.mobileBackButton}
                      onClick={() => history.push("/internal-chat")}
                    >
                      <ArrowBackIcon />
                    </IconButton>
                  ) : null}
                  <Avatar className={classes.avatar}>
                    {activeConversation.type === "group"
                      ? "#"
                      : String(
                          getConversationTitle(activeConversation, user?.id) || "?"
                        )
                          .charAt(0)
                          .toUpperCase()}
                  </Avatar>
                  <div className={classes.chatHeaderText}>
                    <div className={classes.chatHeaderTitleRow}>
                      <span
                        className={classes.presenceDot}
                        style={{
                          backgroundColor: getPresenceColor(
                            getConversationPresenceStatus(
                              activeConversation,
                              user?.id
                            )
                          )
                        }}
                      />
                      <Typography className={classes.chatTitle}>
                        {getConversationTitle(activeConversation, user?.id)}
                      </Typography>
                    </div>
                    <Typography className={classes.chatSubtitle}>
                      {getConversationSubtitleMeta(activeConversation, user?.id)}
                    </Typography>
                  </div>
                </div>
                <div className={classes.headerActions}>
                  <Chip
                    className={`${classes.chipBase} ${classes.chipMeta}`}
                    label={
                      activeConversation.type === "group"
                        ? `${(activeConversation.participants || []).length} participantes`
                        : "Conversa individual"
                    }
                  />
                  {activeConversation.type === "direct" || canManageGroups ? (
                    <Tooltip title="Apagar conversa">
                      <IconButton
                        className={classes.subtleActionButton}
                        onClick={() => setDeleteConfirmOpen(true)}
                      >
                        <DeleteOutlineIcon />
                      </IconButton>
                    </Tooltip>
                  ) : null}
                </div>
              </div>

              <div className={classes.messagesArea} ref={messagesAreaRef}>
                {loadingMessages ? (
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    minHeight={220}
                  >
                    <CircularProgress size={28} />
                  </Box>
                ) : messages.length ? (
                  messages.map(message => {
                    const isMine = Number(message.userId) === Number(user?.id);

                    return (
                      <div
                        key={message.id}
                        className={
                          isMine
                            ? classes.messageRowMine
                            : classes.messageRowOther
                        }
                      >
                        <div
                          className={`${classes.messageBubble} ${
                            isMine
                              ? classes.messageBubbleMine
                              : classes.messageBubbleOther
                          }`}
                        >
                          {!isMine ? (
                            <Typography className={classes.messageAuthor}>
                              {message.user?.name || "Usuario"}
                            </Typography>
                          ) : null}
                          {message.mediaType === "image" && message.mediaUrl ? (
                            <a
                              href={message.mediaUrl}
                              target="_blank"
                              rel="noreferrer"
                            >
                              <img
                                src={message.mediaUrl}
                                alt={message.mediaName || "Imagem enviada"}
                                className={classes.imagePreview}
                              />
                            </a>
                          ) : null}
                          {message.mediaType === "audio" && message.mediaUrl ? (
                            <div className={classes.audioCard}>
                              <Audio
                                url={message.mediaUrl}
                                mimeType={message.mimeType || "audio/ogg"}
                              />
                            </div>
                          ) : null}
                          {(message.mediaType === "application" ||
                            message.mediaType === "file") &&
                          message.mediaUrl ? (
                            <a
                              href={message.mediaUrl}
                              target="_blank"
                              rel="noreferrer"
                              className={classes.documentCard}
                            >
                              <InsertDriveFileOutlinedIcon />
                              <div>
                                <Typography className={classes.documentName}>
                                  {message.mediaName || "Abrir arquivo"}
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                  {message.mimeType === "application/pdf"
                                    ? "PDF"
                                    : "Arquivo"}
                                </Typography>
                              </div>
                            </a>
                          ) : null}
                          {String(message.body || "").trim() ? (
                            <Typography className={classes.messageBody}>
                              {message.body}
                            </Typography>
                          ) : null}
                          <Typography className={classes.messageTime}>
                            {new Date(message.createdAt).toLocaleTimeString("pt-BR", {
                              hour: "2-digit",
                              minute: "2-digit"
                            })}
                          </Typography>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className={classes.emptyState}>
                    <div>
                      <Typography variant="h6">Sem mensagens ainda</Typography>
                      <Typography variant="body2" color="textSecondary">
                        Comece a conversa com sua equipe por aqui.
                      </Typography>
                    </div>
                  </div>
                )}
              </div>

              <div className={classes.composer}>
                {pendingMedia ? (
                  <div className={classes.attachmentPreview}>
                    <div className={classes.attachmentPreviewInfo}>
                      {String(pendingMedia.type || "").startsWith("image/") ? (
                        <ImageOutlinedIcon />
                      ) : (
                        <InsertDriveFileOutlinedIcon />
                      )}
                      <div className={classes.attachmentPreviewText}>
                        <Typography className={classes.attachmentName}>
                          {pendingMedia.name}
                        </Typography>
                        <Typography className={classes.attachmentMeta}>
                          {getPendingMediaLabel(pendingMedia)} • {formatBytes(pendingMedia.size)}
                        </Typography>
                      </div>
                    </div>
                    <div className={classes.composerActions}>
                      <IconButton
                        className={classes.iconActionButton}
                        onClick={() => setPendingMedia(null)}
                        disabled={sending}
                      >
                        <HighlightOffIcon />
                      </IconButton>
                      <IconButton
                        className={classes.sendButton}
                        onClick={handleSendPendingMedia}
                        disabled={sending}
                      >
                        <SendOutlinedIcon />
                      </IconButton>
                    </div>
                  </div>
                ) : null}

                <div className={classes.composerRow}>
                  {recording ? (
                    <div className={classes.recorderWrapper}>
                      <IconButton
                        className={classes.iconActionButton}
                        onClick={handleCancelAudio}
                        disabled={sending}
                      >
                        <HighlightOffIcon />
                      </IconButton>
                      {sending ? (
                        <CircularProgress size={22} className={classes.audioLoading} />
                      ) : (
                        <RecordingTimer />
                      )}
                      <IconButton
                        className={classes.sendButton}
                        onClick={handleSendRecordedAudio}
                        disabled={sending}
                      >
                        <CheckCircleOutlineIcon />
                      </IconButton>
                    </div>
                  ) : (
                    <>
                      <div className={classes.composerActions}>
                        <div className={classes.composerInputWrap}>
                          <IconButton
                            className={classes.iconActionButton}
                            onClick={() => setShowEmoji(prev => !prev)}
                            disabled={sending || startingRecording}
                          >
                            <MoodOutlinedIcon />
                          </IconButton>
                          {showEmoji ? (
                            <div className={classes.emojiBox}>
                              <ClickAwayListener onClickAway={() => setShowEmoji(false)}>
                                <Picker
                                  perLine={16}
                                  showPreview={false}
                                  showSkinTones={false}
                                  onSelect={handleAddEmoji}
                                />
                              </ClickAwayListener>
                            </div>
                          ) : null}
                        </div>
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/jpeg,image/png,application/pdf"
                          className={classes.attachmentInput}
                          onChange={handleSelectMedia}
                        />
                        <IconButton
                          className={classes.iconActionButton}
                          onClick={() => fileInputRef.current?.click()}
                          disabled={sending || startingRecording}
                        >
                          <AttachFileOutlinedIcon />
                        </IconButton>
                        <IconButton
                          className={classes.iconActionButton}
                          onClick={handleStartRecording}
                          disabled={sending || startingRecording || Boolean(pendingMedia)}
                        >
                          {startingRecording ? (
                            <CircularProgress size={20} className={classes.audioLoading} />
                          ) : (
                            <MicOutlinedIcon />
                          )}
                        </IconButton>
                      </div>
                      <TextField
                        className={classes.composerInput}
                        variant="outlined"
                        size="small"
                        multiline
                        rowsMax={4}
                        placeholder="Digite uma mensagem interna"
                        value={composeBody}
                        onChange={event => setComposeBody(event.target.value)}
                        onKeyDown={handleComposerKeyDown}
                      />
                      <IconButton
                        className={classes.sendButton}
                        onClick={handleSendMessage}
                        disabled={sending || !String(composeBody || "").trim()}
                      >
                        <SendOutlinedIcon />
                      </IconButton>
                    </>
                  )}
                </div>
              </div>
            </>
          ) : (
            <div className={classes.emptyState}>
              <div>
                <Typography variant="h5">Selecione uma conversa interna</Typography>
                      <Typography variant="body2" color="textSecondary">
                  O chat interno individual e em grupo fica separado dos tickets,
                  para a equipe colaborar sem misturar com o cliente.
                      </Typography>
              </div>
            </div>
          )}
        </Paper>
        ) : null}
      </div>
    </MainContainer>
  );
};

export default InternalChat;
